import { useEffect, useState, type RefObject } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RoleplayDebrief, RoleplaySessionResponse } from '@wundo/shared/roleplay.ts';
import { DEBRIEF_POLL_MS, DEBRIEF_WAIT_LIMIT_MS } from './debriefWait.ts';
import { endRoleplaySession, fetchRoleplaySession } from '@/lib/roleplaySession.ts';

export interface PracticeEndOptions {
  session: RoleplaySessionResponse;
  /** How to hang up the call, whichever kind it is; null without one. */
  hangUp: RefObject<(() => Promise<void>) | null>;
  /** Asks again for what was practised, now that there is a new attempt. */
  refresh: () => void;
  /** A host that shows the feedback itself; see `PracticeSessionExperience`. */
  onDebrief: ((debrief: RoleplayDebrief | null) => void) | undefined;
  /** The feedback, or null when nobody spoke, to show on this screen. */
  onEndedHere: (debrief: RoleplayDebrief | null) => void;
  /** The end went through, whatever it answered. */
  onAnswered: () => void;
}

/**
 * Ending a practice conversation and waiting for its feedback to be written.
 *
 * `ending` holds from the press until the feedback is there; `debriefFailed`
 * when writing it gave up or never came, and ending again asks once more.
 */
export function usePracticeEnd({
  session,
  hangUp,
  refresh,
  onDebrief,
  onEndedHere,
  onAnswered,
}: PracticeEndOptions) {
  const queryClient = useQueryClient();

  /*
   * Each end that answers `completing` is a new ask for the feedback, with a
   * wait of its own. The wait that ran out is remembered by its ask, so the
   * next one starts clean.
   */
  const [asked, setAsked] = useState(0);
  const [gaveUpOn, setGaveUpOn] = useState<number | null>(null);

  const end = useMutation({
    mutationFn: async () => {
      /*
       * Hang up before asking for the feedback: writing it takes a second or
       * two, and buffered speech would keep playing right through it.
       */
      await hangUp.current?.();
      return endRoleplaySession(session.id);
    },
    onSuccess: (result) => {
      onAnswered();
      refresh();

      /* Being written: the session is read again until it is. */
      if (result.status === 'completing') {
        setAsked((count) => count + 1);
        queryClient.setQueryData<RoleplaySessionResponse>(
          ['roleplaySession', session.id],
          (current) => current && { ...current, status: result.status },
        );
        return;
      }

      /* A host that reads the feedback itself is handed it and takes over. */
      if (onDebrief !== undefined) {
        onDebrief(result.debrief);
        return;
      }

      /* Otherwise it stays where the conversation was, even when nobody spoke. */
      onEndedHere(result.debrief);
    },
    /*
     * Refused, most likely because the feedback was settled after this screen
     * stopped asking. Reading the session again shows where it stands.
     */
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['roleplaySession', session.id] });
    },
  });

  /*
   * Asked for every couple of seconds, but not forever: a job the server lost
   * would keep this screen waiting for nothing. Past the point where the
   * server has settled it either way, the wait counts as failed.
   */
  const waiting = session.status === 'completing';
  const waitedTooLong = waiting && gaveUpOn === asked;
  useEffect(() => {
    if (!waiting) {
      return;
    }

    const timer = setTimeout(() => setGaveUpOn(asked), DEBRIEF_WAIT_LIMIT_MS);
    return () => clearTimeout(timer);
  }, [waiting, asked]);

  useQuery({
    queryKey: ['roleplaySession', session.id],
    queryFn: () => fetchRoleplaySession(session.id),
    enabled: waiting && !waitedTooLong,
    refetchInterval: DEBRIEF_POLL_MS,
  });

  return {
    end: () => end.mutate(),
    endFailed: end.isError,
    ending: end.isPending || (waiting && !waitedTooLong),
    debriefFailed:
      !end.isPending && (session.status === 'debrief_failed' || waitedTooLong),
  };
}
